<template>
  <div>
    <b-card title="Родительский контроль">
      <b-card-text>
        <p>Управление родительскими аккаунтами и связями</p>
      </b-card-text>

      <b-tabs content-class="mt-3">
        <!-- Parents Tab -->
        <b-tab title="Родители" active>
          <div class="d-flex justify-content-between mb-3">
            <h4>Список родителей</h4>
            <b-button variant="primary" size="sm" @click="showCreateModal = true">
              Создать родителя
            </b-button>
          </div>

          <b-table
            :items="parents"
            :fields="parentFields"
            striped
            hover
            responsive
          >
            <template #cell(actions)="row">
              <b-button size="sm" variant="outline-primary" @click="selectParent(row.item)">
                Просмотр
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- Relationships Tab -->
        <b-tab title="Связи">
          <h4 class="mb-3">Связи родитель-студент</h4>
          <b-table
            :items="relationships"
            :fields="relationshipFields"
            striped
            hover
            responsive
          >
            <template #cell(actions)="row">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="confirmDeleteRelationship(row.item.id)"
              >
                Удалить
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- Binding Tab -->
        <b-tab title="Привязка">
          <h4 class="mb-3">Привязать студента к родителю</h4>
          <b-form @submit.prevent="bindStudentToParent">
            <b-row>
              <b-col md="6">
                <b-form-group label="Выберите родителя">
                  <v-select
                    v-model="selectedParent"
                    :options="parents"
                    label="name"
                    :reduce="parent => parent.id"
                    placeholder="Поиск родителя..."
                    @input="bindingForm.parent_id = selectedParent"
                  >
                    <template #option="option">
                      <div>
                        <strong>{{ option.name }}</strong>
                        <br>
                        <small class="text-muted">{{ option.email }}</small>
                      </div>
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Выберите студента">
                  <v-select
                    v-model="selectedStudent"
                    :options="studentsForBinding"
                    label="name"
                    :reduce="student => student.id"
                    placeholder="Поиск студента..."
                    @input="bindingForm.student_id = selectedStudent"
                  >
                    <template #option="option">
                      <div>
                        <strong>{{ option.name }}</strong>
                        <br>
                        <small class="text-muted">{{ option.email }} | Баланс: {{ option.balance }}</small>
                      </div>
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group label="Заметки (необязательно)">
              <b-form-textarea
                v-model="bindingForm.notes"
                placeholder="Дополнительные заметки..."
              />
            </b-form-group>
            <b-button
              type="submit"
              variant="primary"
              :disabled="!bindingForm.parent_id || !bindingForm.student_id"
            >
              Создать связь
            </b-button>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Create Parent Modal -->
    <CreateParentModal
      v-model="showCreateModal"
      @parent-created="onParentCreated"
    />

    <!-- Binding Modal -->
    <BindingModal
      v-model="showBindingModal"
      :selected-parent="selectedParentData"
      @binding-created="onBindingCreated"
    />
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardText,
  BFormGroup,
  BFormTextarea,
  BFormSelect,
  BRow,
  BCol,
  BForm,
  BTabs,
  BTab,
  BTable
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import CreateParentModal from './CreateParentModal.vue'
import BindingModal from './BindingModal.vue'
import ParentalControlAPI from '@/api/parentalControl'

export default {
  name: 'ParentalControlAdmin',
  components: {
    BButton,
    BCard,
    BCardText,
    BFormGroup,
    BFormTextarea,
    BFormSelect,
    BRow,
    BCol,
    BForm,
    BTabs,
    BTab,
    BTable,
    vSelect,
    CreateParentModal,
    BindingModal
  },
  data() {
    return {
      showCreateModal: false,
      showBindingModal: false,
      parents: [],
      relationships: [],
      studentsForBinding: [],
      selectedParentData: null,
      selectedParent: null,
      selectedStudent: null,
      bindingForm: {
        parent_id: null,
        student_id: null,
        notes: ''
      },
      parentFields: [
        { key: 'name', label: 'Имя' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Телефон' },
        { key: 'students_count', label: 'Студентов' },
        { key: 'actions', label: 'Действия' }
      ],
      relationshipFields: [
        { key: 'parent.name', label: 'Родитель' },
        { key: 'student.name', label: 'Студент' },
        { key: 'created_at', label: 'Создано' },
        { key: 'is_active', label: 'Статус' },
        { key: 'actions', label: 'Действия' }
      ]
    }
  },
  methods: {
    async loadParents() {
      try {
        const response = await ParentalControlAPI.getParents()
        this.parents = response.data
      } catch (error) {
        this.showError('Ошибка загрузки родителей')
      }
    },
    async loadRelationships() {
      try {
        const response = await ParentalControlAPI.getRelationships()
        this.relationships = response.data
      } catch (error) {
        this.showError('Ошибка загрузки связей')
      }
    },
    async loadStudentsForBinding() {
      try {
        const response = await ParentalControlAPI.getStudentsForBinding()
        this.studentsForBinding = response.data
      } catch (error) {
        this.showError('Ошибка загрузки студентов')
      }
    },
    selectParent(parent) {
      this.selectedParentData = parent
      this.showBindingModal = true
    },
    async bindStudentToParent() {
      try {
        await ParentalControlAPI.bindStudent(this.bindingForm)
        this.showSuccess('Студент успешно привязан к родителю')
        // Reset form and selections
        this.bindingForm = { parent_id: null, student_id: null, notes: '' }
        this.selectedParent = null
        this.selectedStudent = null
        await this.loadRelationships()
        await this.loadStudentsForBinding()
      } catch (error) {
        this.showError('Ошибка при привязке студента')
      }
    },
    async confirmDeleteRelationship(relationshipId) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Вы уверены, что хотите удалить эту связь?',
        {
          title: 'Подтверждение удаления',
          okVariant: 'danger',
          okTitle: 'Удалить',
          cancelTitle: 'Отмена',
          centered: true
        }
      )
      if (confirmed) {
        try {
          await ParentalControlAPI.deleteRelationship(relationshipId)
          this.showSuccess('Связь успешно удалена')
          await this.loadRelationships()
          await this.loadParents()
        } catch (error) {
          this.showError('Ошибка при удалении связи')
        }
      }
    },
    onParentCreated() {
      this.showSuccess('Родитель успешно создан')
      this.loadParents()
    },
    onBindingCreated() {
      this.showSuccess('Привязка создана успешно')
      this.loadRelationships()
      this.loadStudentsForBinding()
    },
    showSuccess(message) {
      this.$bvToast.toast(message, {
        title: 'Успех',
        variant: 'success',
        solid: true,
      })
    },
    showError(message) {
      this.$bvToast.toast(message, {
        title: 'Ошибка',
        variant: 'danger',
        solid: true,
      })
    }
  },
  async created() {
    await Promise.all([
      this.loadParents(),
      this.loadRelationships(),
      this.loadStudentsForBinding()
    ])
  }
}
</script>

<style lang="scss" scoped>
.card {
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 25px 0 rgba(0,0,0,.1);
}
</style>
